import moment from 'moment';
import '../../common/bn-card';
import '../../common/bn-icon';
import '../../common/bn-icon-bin';
import '../../common/bn-loading';
import '../../common/bn-property';
import '../../organizations/parts/farm-address';
import '../../organizations/parts/organization-ancestors-bread-crumbs';
import '../../notes/site-notes';
import { LitEntity, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { formatDateLong, getDateFromString } from '../../utility/dates';

class ScheduleLocations extends LitEntity {
  constructor() {
    super();
    this.locations = [];
  }

  static get properties() {
    return {
      locations: { type: Array },
      today: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .no-data {
        text-align: center;
        padding: 1em;
      }
      .content {
        display: flex;
        align-items: center;
      }
      .farm-name {
        flex-grow: 1;
      }
      h2 {
        margin: 0 .5rem .5rem;
      }
      .order {
        font-size: 1.2em;
        padding: .2em .5em;
        margin-right: 1em;
        font-weight: bold;
        color: var(--blue-60);
        background-color: var(--blue-40);
        border-radius: 1em;
        width: 1em;
        text-align: center;
      }
      .farm-icon {
        color: var(--blue-60);
        margin-right: 1rem;
        font-size: 1.5rem;
      }
      organization-ancestors-bread-crumbs {
        margin-bottom: .5rem;
        display: block;
        font-weight: bold;
      }
      farm-address {
        color: var(--gray-80);
      }
      .count {
        display: flex;
        align-items: center;
        margin: 0 .5rem;
      }
      bn-icon-bin {
        height: 1.5em;
        width: 1em;
        margin: 0 .5em 0 0;
      }
      @media(max-width: 800px) {
        .content {
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
        }
        .order {
          margin: 0;
          order: 2;
        }
        .farm-icon {
          order: 1;
          margin: 0;
        }
        .count {
          order: 3;
          margin: 0;
        }
        .farm-name {
          margin-top: 1rem;
          order: 4;
          width: 100%;
        }
        .reschedule {
          width: 100%;
          order: 5;
          display: flex;
          justify-content: center;
          margin: 1rem 0 0 0;
        }
        .handle {
          display: none;
        }
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`<bn-loading></bn-loading>`;
    }
    if (!this.locations.length) {
      return html`
        <p class="no-data">There are no Work Order Locations to display.</p>
      `;
    }
    const groups = this.locations.reduce((g, l) => {
      const date = l.properties.scheduledFor;
      (g.get(date) || g.set(date, []).get(date)).push(l);
      return g;
    }, new Map());
    return html`
      ${Array.from(groups).map(([date, locations]) => html`
        <h2>${formatDateLong(getDateFromString(date))}</h2>
        <div class="locations">
          ${locations.map((location) => this._renderLocation(location))}
        </div>
      `)}
    `;
  }

  _renderLocation(location) {
    const farmHref = this.getHref('https://api.binsentry.com/rel/farm', location);
    return html`
      <bn-card class="location" .href="/work-order-schedule/work-orders?locationId=${encodeURIComponent(this.getHref('self', location))}">
        <div class="content">
          <div class="order">
            ${this.getProperty('order', '', location)}
          </div>

          <div class="farm-icon">
            <bn-icon name="fad fa-farm"></bn-icon>
          </div>

          <div class="farm-name">
            <organization-ancestors-bread-crumbs
              .href="${farmHref}"
              ancestorMinIndex="0"
              stop-at-type="binsentry"
            ></organization-ancestors-bread-crumbs>

            <farm-address .href="${farmHref}" include-map-links></farm-address>
          </div>

          <div class="count">
            <bn-icon-bin></bn-icon-bin>
            ${this.getProperty('workOrderCount', 0, location)}
          </div>
        </div>
        <site-notes href="${farmHref}" readonly></site-notes>
      </bn-card>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    const locations = (this.entity && this.entity.getSubEntitiesByClass('work-order-location')) || [];
    this.locations = this.today ? locations.filter((loc) => this._isToday(loc.properties.scheduledFor)) : locations;
  }

  _isToday(scheduledFor) {
    return moment(scheduledFor).isBefore(moment().add(1, 'day'), 'day');
  }
}

customElements.define('schedule-locations', ScheduleLocations);
